import React, { useCallback, useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { myPrimaryColor } from '../../constants/colors'
import { useAppCompanyState, useAppSiteState, useConfigState } from '../../context/AppContext/AppContext'
import { ConditionalTooltip } from './ConditionalTooltip'
import { useConfigActions } from '../../actions/configActions'
import { useCompanyActions } from '../../actions/companyActions'
import { getCurrentUser } from '../../libs/authLib'
import { useSiteActions } from '../../actions/siteActions'

// Constants
const NO_COMPANY_SELECTED = '0';
const NO_SITE_SELECTED = '0';
const SINGLE_SITE_COUNT = 1;

// Note: The `companyName` prop has been removed from this interface.
// The component now internally derives `companyName` from context/state (siteState).
// `companyName` is now derived from context/state.
interface CompanySiteHeaderProps {
  siteName?: string
  isPureDesignUser?: boolean
  maxWidth?: number
}

// Helper function to check if a company is selected but no site is selected
const isCompanySelectedWithoutSite = (selectedCompanyId: string | undefined, selectedSiteIdForCompany: string | undefined): boolean => {
  return !!(selectedCompanyId && selectedCompanyId !== NO_COMPANY_SELECTED && (!selectedSiteIdForCompany || selectedSiteIdForCompany === NO_SITE_SELECTED));
};

interface CompanySite {
  id: string;
  name: string;
  companyName: string;
}

// Helper function to get the display company name
const getDisplayCompanyName = (
  isPureDesignUser: boolean, 
  companyName?: string, 
  selectedCompanyId?: string, 
  companySites?: CompanySite[]
): string => {
  if (isPureDesignUser) {
    return companyName || '';
  }

  if (selectedCompanyId && selectedCompanyId !== NO_COMPANY_SELECTED) {
    // All sites belong to the same company, so we can get the company name directly from the first element
    // Find the first site with a non-empty companyName
    return companySites?.find(site => site.companyName && site.companyName.trim() !== '')?.companyName || '';
  } else {
    return '';
  }
};

// Helper function to get the display site name
const getDisplaySiteName = (
  isPureDesignUser: boolean,
  siteName?: string,
  selectedCompanyId?: string,
  selectedSiteIdForCompany?: string,
  companySites?: CompanySite[]
): string => {
  if (isPureDesignUser) {
    return siteName || '';
  }

  if (isCompanySelectedWithoutSite(selectedCompanyId, selectedSiteIdForCompany)) {
    return '';
  } else {
    return companySites?.find((site) => site.id === selectedSiteIdForCompany)?.name || '';
  }
};

const CompanySiteHeader: React.FC<CompanySiteHeaderProps> = ({
  siteName,
  isPureDesignUser = false,
  maxWidth = 2000,
}) => {

  const companyActions = useCompanyActions();
  const siteActions = useSiteActions();
  const configActions = useConfigActions();
  const configState = useConfigState();
  const siteState = useAppSiteState();
  const { companyName } = siteState;

  const [isCompanyDataLoading, setIsCompanyDataLoading] = useState<boolean>(false);

  const performSiteChange = useCallback(
    async (siteId: string) => {
      const currentUser = await getCurrentUser();
      if (currentUser && siteId) {
        const userSite = await siteActions.loadSingleSite(siteId);
        const vrsSite = await companyActions.getPlantAppSync(siteId);
        if (userSite) {
          configActions.setAppDataInitialization("firstSiteData", (state) => ({
            ...state,
            loaded: true,
            isExternal: vrsSite?.isExternal,
          }));
        }
      }
      await siteActions.setSite(siteId, currentUser);
    },
    [configActions, siteActions, companyActions]
  );

  const siteChangedHandler = useCallback(
    (siteId: string) => {
      const activeSiteId = siteId || NO_SITE_SELECTED;
      companyActions.setVrsCompanySiteId(activeSiteId);
      if (activeSiteId !== NO_SITE_SELECTED) {
        performSiteChange(siteId);
      }
    },
    [companyActions, performSiteChange]
  );

  const {
    selectedCompanyId,
    selectedSiteIdForCompany,
    companySites,
    companyInitiated,
    companyLoaded,
  } = useAppCompanyState();

  // Custom tooltip styles
  const tooltipStyles = {
    tooltip: {
      backgroundColor: 'white',
      color: myPrimaryColor,
      fontSize: '12px',
      fontFamily: 'Poppins, sans-serif',
      border: '1px solid rgba(0, 0, 0, 0.12)',
      borderRadius: '4px',
      padding: '8px 12px',
      maxWidth: '300px',
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
    }
  }

  useEffect(() => {
    if (isPureDesignUser) {
      // For pure design users, we don't need to load company data
      return;
    }

    if (
      configState.appDataInitialization.companyAndSiteSelectorData
        .refreshRequired ||
      (!companyInitiated && !companyLoaded && !isCompanyDataLoading)
    ) {
      configActions.setAppDataInitialization(
        "companyAndSiteSelectorData",
        (state) => ({
          ...state,
          refreshRequired: false,
        })
      );
      setIsCompanyDataLoading(true);
      companyActions
        .getCompaniesAppSync(false)
        .then(() => {
          configActions.setAppDataInitialization(
            "companiesInitialization",
            (state) => ({
              ...state,
              loaded: true,
            })
          );

          if (selectedCompanyId !== NO_COMPANY_SELECTED) {
            companyActions
              .getPlantsAppSync(
                selectedCompanyId,
                false
              )
              .then((sites) => {
                if (sites && sites.length === SINGLE_SITE_COUNT) {
                  siteChangedHandler(sites[0].id);
                }
              });
          }
        })
        .finally(() => {
          setIsCompanyDataLoading(false);
        });
    }
  }, [
    isPureDesignUser,
    companyActions,
    configActions,
    isCompanyDataLoading,
    companyInitiated,
    companyLoaded,
    selectedCompanyId,
    siteChangedHandler,
    configState.appDataInitialization?.companyAndSiteSelectorData
      ?.refreshRequired,
  ]);

  const displayCompanyName = getDisplayCompanyName(isPureDesignUser, companyName, selectedCompanyId, companySites);

  const displaySiteName = getDisplaySiteName(isPureDesignUser, siteName, selectedCompanyId, selectedSiteIdForCompany, companySites);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        maxWidth: `${maxWidth}px`,
        minWidth: 0,
        overflow: 'hidden',
        width: 'fit-content',
      }}
    >
      {!isPureDesignUser && (
        <ConditionalTooltip
          tooltipText={displayCompanyName || ''}
          tooltipStyles={tooltipStyles}
          maxWidth={maxWidth}
        >
          {displayCompanyName}
        </ConditionalTooltip>
      )}

      {!isPureDesignUser && displayCompanyName && displaySiteName && (
        <Typography
          variant={'h6'}
          sx={{
            fontWeight: 700,
            fontSize: '12px',
            color: myPrimaryColor,
            mx: 1,
            flexShrink: 0,
          }}
        >
          -
        </Typography>
      )}

      <ConditionalTooltip
        tooltipText={displaySiteName || ''}
        tooltipStyles={tooltipStyles}
        maxWidth={maxWidth}
      >
        {displaySiteName}
      </ConditionalTooltip>
    </Box>
  )
}

export default CompanySiteHeader
